/**
 * The Money Meeting MUI theme (TMMAPP-0001, brand roles TMMAPP-0035) — maps the active brand
 * theme's roles onto a real MUI theme, per mode. `buildAppTheme(mode, density, brand)` wires the
 * palette, typography (the brand's body / heading / mono stacks on the prototype's scale, TMM-T2
 * 1.4), shape, shadows, the prototype's chrome (TMMAPP-0099, TMM-T6 1.4) and component density
 * from tokens, so both the
 * palette-driven surfaces and the overrides below follow the brand and flip per mode. Mounted
 * once app-wide via `AppThemeProvider`. `resolveTokens(theme)` reads `theme.tokens` with a safe
 * fallback (the built-in theme) for a bare `createTheme()` (component tests that don't mount the
 * app theme).
 *
 * Status palettes carry their `Fill` as `main` (solid controls, with `Ink` as `contrastText`);
 * wherever MUI renders a status colour as TEXT (text / outlined buttons, outlined chips, the
 * standard Alert, form error labels) the override below uses `<status>Text`, and as an icon
 * `<status>Fg` — the roles saas-auth guarantees on surfaces. On a `<status>Soft` wash (the
 * standard Alert's icon) the guaranteed role is `<status>Text`. Brand-coloured normal text is
 * `link` (4.5:1); `brand` itself is only guaranteed 3:1. A non-text brand boundary on a canvas
 * (checked controls, progress, the tab indicator, outlined primary borders) is the served
 * `brandBoundary` (SASATH-0352, TMMAPP-0045), guaranteed 3:1 on bg / surface / surfaceRaised.
 */
import { createTheme, type Shadows, type Theme } from '@mui/material/styles';
import type { CSSProperties } from 'react';
import type { Density } from '@/context/density-context';
import {
  isFigtree,
  isInter,
  isJetBrainsMono,
  isNewsreader,
  type BrandFont,
  type BrandTheme,
} from './brandTheme';
import { SelectCaret } from '@/components/icons';
import { TEXT_LABEL } from './textLabel';
import { PLATFORM_DEFAULT_THEME } from './platformDefaultTheme';
import { raisedLine } from './raisedLine';
import { PILL_RADIUS, tokensFromBrand, type AppTokens, type ThemeMode } from './tokens';

/** The headline-money variants (visual contract v2 §13, TMMAPP-0150), largest first. */
export const FIGURE_VARIANTS = [
  'figureXl',
  'figureHero',
  'figureLg',
  'figure',
  'figureSm',
] as const;
export type FigureVariant = (typeof FIGURE_VARIANTS)[number];

declare module '@mui/material/styles' {
  interface TypographyVariants {
    /** Identifiers, references, audit timestamps, raw previews, version labels (TMM-T1 1.4). */
    mono: CSSProperties;
    /** The import's raw CSV preview: mono at 12px (prototype-visual-contract §1, P:1746). */
    monoSmall: CSSProperties;
    /** The Home greeting: the heading face at 42 / 46 (visual contract v2 §2, TMMAPP-0150). */
    display: TypographyVariants['h1'];
    /** A small tracked label above a page title (TMM-T2 1.4). */
    eyebrow: CSSProperties;
    /** A card's kind ("BUDGET"), as text (TMM-T2 1.4). */
    kindLabel: CSSProperties;
    /** Tags, chart axis labels, kind pills: the 10px floor (TMM-T2 1.4). */
    micro: CSSProperties;
    /** Net worth (42 / 46). */
    figureXl: CSSProperties;
    /** The envelope page headline (38 / 42). */
    figureHero: CSSProperties;
    /** The virtual account headline (34 / 40). */
    figureLg: CSSProperties;
    /** An account card balance (28 / 32). */
    figure: CSSProperties;
    /** An envelope card balance (26 / 31). */
    figureSm: CSSProperties;
    /** A tile's figure: the body face at 600, 22 / 28 (visual contract v2 §2, TMMAPP-0150). */
    stat: CSSProperties;
    /** A compact stat tile's value, six in one row (18 / 24, 400; TMMAPP-0140 item 5). */
    statSm: CSSProperties;
  }
  interface TypographyVariantsOptions {
    mono?: CSSProperties;
    monoSmall?: CSSProperties;
    // A heading-style option, so it can carry its phone-width media rule like h1.
    display?: TypographyVariantsOptions['h1'];
    eyebrow?: CSSProperties;
    kindLabel?: CSSProperties;
    micro?: CSSProperties;
    figureXl?: CSSProperties;
    figureHero?: CSSProperties;
    figureLg?: CSSProperties;
    figure?: CSSProperties;
    figureSm?: CSSProperties;
    stat?: CSSProperties;
    statSm?: CSSProperties;
  }
  interface Theme {
    tokens: AppTokens;
    /** The active layout density (TMMAPP-0004) — spacing only, never a color. */
    density: Density;
  }
  interface ThemeOptions {
    tokens?: AppTokens;
    density?: Density;
  }
}
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    mono: true;
    monoSmall: true;
    display: true;
    eyebrow: true;
    kindLabel: true;
    micro: true;
    figureXl: true;
    figureHero: true;
    figureLg: true;
    figure: true;
    figureSm: true;
    stat: true;
    statSm: true;
  }
}
declare module '@mui/material/Table' {
  interface TablePropsSizeOverrides {
    /** The envelope register's tighter cells (visual contract §10). */
    dense: true;
  }
}
declare module '@mui/material/TableCell' {
  interface TableCellPropsSizeOverrides {
    dense: true;
  }
}
declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    /** A paper inside a content card: no second border or shadow (TMMAPP-0088). */
    nested: true;
  }
}

/**
 * TMM-T1 (TMMAPP-0069): Inter's `cv05` (tailed `l`) and `cv08` (serifed `I`) character variants
 * keep `I`, `l` and `1` distinct. Applied only when the resolved brand family is Inter — a no-op
 * for any other face, so it is not set for them.
 */
const INTER_FEATURES = '"cv05", "cv08"';
const inter = (family: string): Pick<CSSProperties, 'fontFeatureSettings'> =>
  isInter(family) ? { fontFeatureSettings: INTER_FEATURES } : {};

/**
 * TMM-T1 (TMMAPP-0084, 0085): a face whose family is Inter renders from the bundled `inter-ui` copy
 * (`InterVariable`, `fonts.ts`), which carries `cv05` / `cv08`; one whose family is JetBrains Mono
 * renders from the bundled `@fontsource-variable/jetbrains-mono` copy (`JetBrains Mono Variable`).
 * TMMAPP-0150: the v2 faces Figtree and Newsreader render from their bundled `@fontsource-variable`
 * copies (`Figtree Variable`, `Newsreader Variable`) the same way. The brand's own stack stays
 * after it as the fallback. Any other face's stack is used as delivered.
 */
const BUNDLED_FAMILIES: [(family: string) => boolean, string][] = [
  [isInter, 'InterVariable'],
  [isJetBrainsMono, "'JetBrains Mono Variable'"],
  [isFigtree, "'Figtree Variable'"],
  [isNewsreader, "'Newsreader Variable'"],
];
const fontStack = (font: BrandFont): string => {
  const bundled = BUNDLED_FAMILIES.find(([matches]) => matches(font.family));
  return bundled ? `${bundled[1]}, ${font.stack}` : font.stack;
};

export function resolveTokens(theme: Theme): AppTokens {
  return (
    (theme as { tokens?: AppTokens }).tokens ??
    tokensFromBrand(PLATFORM_DEFAULT_THEME, theme.palette.mode === 'dark' ? 'dark' : 'light')
  );
}

const STATUSES = ['success', 'warning', 'error', 'info'] as const;
type Status = (typeof STATUSES)[number];

const statusRole = (t: AppTokens, status: Status, part: 'Fg' | 'Text' | 'Fill' | 'Ink' | 'Soft') =>
  t[`${status}${part}`];

/**
 * A default-colour chip whose icon takes the chip's colour (MUI's `iconColor === color` owner
 * state) — the case MUI paints `grey[700]` / `grey[300]`.
 */
const ownDefaultIcon = (props: object) => {
  const { color, iconColor } = props as { color?: string; iconColor?: string };
  return color === 'default' && iconColor === 'default';
};

/**
 * A text button or IconButton in an alert's or a snackbar's action slot (TMMAPP-0054, snackbar
 * TMMAPP-0055) paints its host's own ink and never fills: a `hover` layer over the alert's wash or
 * the snackbar's inverse `text` is a pair saas-auth does not guarantee. Hover underlines a text
 * button and rings an IconButton, keyboard focus is an outline, both in that ink (currentColor),
 * and the ripple (a translucent fill) is hidden. A disabled one keeps the ink too.
 */
const quietActionSlot = {
  '& .MuiButton-text, & .MuiIconButton-root': {
    color: 'inherit',
    '&:hover, &.Mui-focusVisible': {
      '--variant-textBg': 'transparent',
      '--IconButton-hoverBg': 'transparent',
      backgroundColor: 'transparent',
    },
    '&.Mui-focusVisible': { outline: '2px solid currentColor', outlineOffset: 1 },
    '& .MuiTouchRipple-root': { display: 'none' },
  },
  '& .MuiButton-text:hover': { textDecoration: 'underline' },
  '& .MuiIconButton-root:hover': { boxShadow: 'inset 0 0 0 1px currentColor' },
};

/** MUI's 25 elevation levels from the brand's three: 1 card, 2–7 raised, 8+ dialog. */
function shadowsFrom(t: AppTokens): Shadows {
  return Array.from({ length: 25 }, (_, level) =>
    level === 0
      ? 'none'
      : level === 1
        ? t.elevation.card
        : level < 8
          ? t.elevation.raised
          : t.elevation.dialog,
  ) as Shadows;
}

/**
 * The opaque base a state layer is painted over: `surface`, or `surfaceRaised` inside a menu,
 * popover or dialog (their papers set it). A CSS custom property, so it follows the DOM.
 */
const LAYER_BASE = '--tmm-layer-base';

export { TEXT_LABEL } from './textLabel';

/**
 * A state layer painted as an opaque composite over its surface (TMMAPP-0099): `link` / `text2`
 * over `surface | surfaceRaised + selected | hover` are guaranteed pairs wherever the control
 * sits, while the translucent layer alone over the page `bg` is not. On a card it is
 * pixel-identical. Used by the selected toggle, the outlined-button hover and the kind pills.
 */
export function layerOverSurface(t: AppTokens, layer: string): string {
  return `linear-gradient(${layer}, ${layer}), var(${LAYER_BASE}, ${t.surface})`;
}

/**
 * Phone width, MUI's `xs` (`theme.breakpoints.down('sm')` before the theme exists): the v2 h1 is 30
 * and the Home greeting 32 there (visual contract v2 §15).
 */
const XS_ONLY = '@media (max-width:599.95px)';

/** Size / line height of each headline figure (visual contract v2 §2 and §13, TMMAPP-0150). */
const FIGURE_SIZES: Record<FigureVariant, [number, number]> = {
  figureXl: [42, 46],
  figureHero: [38, 42],
  figureLg: [34, 40],
  figure: [28, 32],
  figureSm: [26, 31],
};

function figureVariants(
  family: string,
  features: Pick<CSSProperties, 'fontFeatureSettings'>,
): Record<FigureVariant, CSSProperties> {
  return Object.fromEntries(
    FIGURE_VARIANTS.map((variant) => {
      const [size, line] = FIGURE_SIZES[variant];
      return [
        variant,
        {
          fontFamily: family,
          ...features,
          fontSize: size,
          lineHeight: `${line}px`,
          fontWeight: 400,
          letterSpacing: 0,
          fontVariantNumeric: 'tabular-nums',
        },
      ];
    }),
  ) as Record<FigureVariant, CSSProperties>;
}

/**
 * Build the mode-aware MUI theme — every color below reads from tokens. `density` (TMMAPP-0004)
 * varies spacing only: table-cell padding, dense list buttons, the dense toolbar, button
 * min-height and chip height. Nothing else — and no color — depends on it.
 */
export function buildAppTheme(
  mode: ThemeMode,
  density: Density = 'comfortable',
  brand: BrandTheme = PLATFORM_DEFAULT_THEME,
): Theme {
  const t = tokensFromBrand(brand, mode);
  // Dividers and skeletons on a raised surface use a colour that is not the fill. A raised surface
  // is the label canvas `surfaceRaised` (`textLabel`, TMMAPP-0099).
  const raisedGuards = {
    [LAYER_BASE]: t.surfaceRaised,
    [TEXT_LABEL]: t.textLabel('surfaceRaised'),
    '& .MuiDivider-root': { borderColor: raisedLine(t) },
    '& .MuiSkeleton-root': { backgroundColor: raisedLine(t) },
  };
  // The prototype's `text3` labels paint `textLabel` for the canvas they sit on (TMMAPP-0099): the
  // page sets the `bg` value, a Paper the `surface` value, a raised paper the `surfaceRaised` value,
  // and a label reads the nearest one (a CSS custom property follows the DOM).
  const label = `var(${TEXT_LABEL}, ${t.textLabel('surface')})`;
  const overSurface = (layer: string) => layerOverSurface(t, layer);
  const { body, heading, mono } = brand.fonts;
  const compact = density === 'compact';
  const statusPalette = (status: Status) => ({
    main: statusRole(t, status, 'Fill'),
    dark: statusRole(t, status, 'Fill'),
    light: statusRole(t, status, 'Soft'),
    contrastText: statusRole(t, status, 'Ink'),
  });
  const headingFont = { fontFamily: fontStack(heading) };

  return createTheme({
    tokens: t,
    density,
    palette: {
      mode,
      primary: { main: t.brand, dark: t.brandHover, light: t.brandSoft, contrastText: t.brandInk },
      secondary: {
        main: t.accent,
        dark: t.accentHover,
        light: t.accentSoft,
        contrastText: t.accentInk,
      },
      success: statusPalette('success'),
      warning: statusPalette('warning'),
      error: statusPalette('error'),
      info: statusPalette('info'),
      background: { default: t.bg, paper: t.surface },
      text: { primary: t.text, secondary: t.text2, disabled: t.text3 },
      divider: t.line,
      // Every action colour is a brand role, so no MUI default neutral reaches the screen.
      action: {
        active: t.text2,
        hover: t.hover,
        selected: t.selected,
        focus: t.hover,
        disabled: t.text3,
        disabledBackground: t.surface2,
      },
    },
    shape: { borderRadius: t.radius.control },
    shadows: shadowsFrom(t),
    // TMM-T2 1.4 (TMMAPP-0099): the prototype's scale. Every variant states size, line height and
    // weight. Sizes are numbers (px); line heights are px strings, because a bare number is a
    // unitless multiplier. Nothing is below 10px. TMMAPP-0150 (visual contract v2 §2): the v2 scale
    // for the headings, the Home `display` greeting, the capitals `overline` and the 15 / 1.45
    // body; h1-h5 and display are the heading face at 400.
    typography: {
      fontFamily: fontStack(body),
      display: {
        ...headingFont,
        ...inter(heading.family),
        fontSize: 42,
        lineHeight: '46px',
        fontWeight: 400,
        letterSpacing: 0,
        [XS_ONLY]: { fontSize: 32, lineHeight: '36px' },
      },
      h1: {
        ...headingFont,
        ...inter(heading.family),
        fontSize: 38,
        lineHeight: '42px',
        fontWeight: 400,
        letterSpacing: 0,
        [XS_ONLY]: { fontSize: 30, lineHeight: '34px' },
      },
      h2: {
        ...headingFont,
        ...inter(heading.family),
        fontSize: 26,
        lineHeight: '30px',
        fontWeight: 400,
        letterSpacing: 0,
      },
      h3: {
        ...headingFont,
        ...inter(heading.family),
        fontSize: 22,
        lineHeight: '28px',
        fontWeight: 400,
      },
      h4: {
        ...headingFont,
        ...inter(heading.family),
        fontSize: 21,
        lineHeight: '26px',
        fontWeight: 400,
      },
      h5: {
        ...headingFont,
        ...inter(heading.family),
        fontSize: 19,
        lineHeight: '24px',
        fontWeight: 400,
      },
      h6: {
        ...headingFont,
        ...inter(heading.family),
        fontSize: 14,
        lineHeight: '21px',
        fontWeight: 500,
        letterSpacing: 0,
      },
      subtitle1: {
        ...inter(body.family),
        fontSize: 15,
        lineHeight: '22px',
        fontWeight: 500,
      },
      subtitle2: {
        ...inter(body.family),
        fontSize: 14,
        lineHeight: '21px',
        fontWeight: 500,
      },
      body1: { ...inter(body.family), fontSize: 15, lineHeight: '22px', fontWeight: 400 },
      body2: {
        ...inter(body.family),
        fontSize: 13,
        lineHeight: '20px',
        fontWeight: 400,
        color: t.text2,
      },
      caption: { ...inter(body.family), fontSize: 12, lineHeight: '18px', fontWeight: 400 },
      // The capitals label (VIEWING, COMING UP, table heads): uppercase by style, sentence case in
      // the catalog (visual contract v2 §2).
      overline: {
        ...inter(body.family),
        fontSize: 12,
        lineHeight: '16px',
        fontWeight: 400,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: label,
      },
      // Buttons, toggle buttons and tabs read as their catalog copy, sentence case (visual contract
      // v2 §7, TMMAPP-0154 §A 3).
      button: {
        ...inter(body.family),
        textTransform: 'none',
        fontWeight: 500,
        letterSpacing: 0,
        fontSize: 14,
        lineHeight: '24px',
      },
      // TMM-T1 1.4: identifiers, references, audit timestamps, raw file previews and version
      // labels — never an amount (amounts are the body face, tabular: `MoneyText`).
      mono: {
        fontFamily: fontStack(mono),
        // `font-feature-settings` inherits, so a mono run inside a body variant (or on `<body>`,
        // where CssBaseline spreads body1) would take Inter's `cv05` / `cv08` — which mean other
        // glyphs in another face. `normal` resets it unless the mono family is itself Inter.
        fontFeatureSettings: inter(mono.family).fontFeatureSettings ?? 'normal',
        fontSize: 14,
        lineHeight: '20px',
        letterSpacing: 0,
        fontVariantNumeric: 'tabular-nums',
      },
      // The import's raw CSV preview (TMMAPP-0139 item 4): the same face at the prototype's 12px.
      monoSmall: {
        fontFamily: fontStack(mono),
        fontFeatureSettings: inter(mono.family).fontFeatureSettings ?? 'normal',
        fontSize: 12,
        lineHeight: '16px',
        letterSpacing: 0,
        fontVariantNumeric: 'tabular-nums',
      },
      // Custom variants name their family explicitly: MUI gives a custom variant no `fontFamily`.
      eyebrow: {
        fontFamily: fontStack(body),
        ...inter(body.family),
        fontSize: 12,
        lineHeight: '16px',
        fontWeight: 500,
        letterSpacing: '0.09em',
        textTransform: 'uppercase',
        color: label,
      },
      kindLabel: {
        fontFamily: fontStack(body),
        ...inter(body.family),
        fontSize: 11,
        lineHeight: '16px',
        fontWeight: 500,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: label,
      },
      micro: {
        fontFamily: fontStack(body),
        ...inter(body.family),
        fontSize: 10,
        lineHeight: '14px',
        fontWeight: 500,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      },
      // Headline money (a balance, a net worth, a headline total): the HEADING face at 400, with
      // tabular numerals so a column of them aligns (visual contract v2 §13; a TMM-B1 / T2 deviation,
      // §14). A tile's figure (`stat`) stays in the body face at 600.
      ...figureVariants(fontStack(heading), inter(heading.family)),
      stat: {
        fontFamily: fontStack(body),
        ...inter(body.family),
        fontSize: 22,
        lineHeight: '28px',
        fontWeight: 600,
        letterSpacing: 0,
        fontVariantNumeric: 'tabular-nums',
      },
      statSm: {
        fontFamily: fontStack(body),
        ...inter(body.family),
        fontSize: 18,
        lineHeight: '24px',
        fontWeight: 400,
        letterSpacing: 0,
        fontVariantNumeric: 'tabular-nums',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: t.bg, [TEXT_LABEL]: t.textLabel('bg') },
          // TMMAPP-0141: an element focused by code (a heading a step moves focus to) follows the
          // dialog title's rule: no outline after a pointer action, the `focusRing` on keyboard focus.
          // `:where()` keeps the specificity at zero, so a component's own focus style still wins.
          ':where([tabindex="-1"]:focus:not(:focus-visible))': { outline: 'none' },
          ':where([tabindex="-1"]:focus-visible)': {
            outline: `2px solid ${t.focusRing}`,
            outlineOffset: 2,
          },
        },
      },
      MuiLink: {
        styleOverrides: { root: { color: t.link } },
      },
      MuiTypography: {
        // A large figure or stat value is a block: it renders a `div`. `subtitle1` / `subtitle2` are
        // text styles, not headings: MUI's own mapping is `h6`, which injects a heading that skips
        // levels (heading-order, A11Y-012), so they render a `p`; a subtitle that is a section
        // heading says so with `component="h3"`. MUI falls back to its own mapping otherwise.
        defaultProps: {
          variantMapping: {
            ...Object.fromEntries(FIGURE_VARIANTS.map((variant) => [variant, 'div'])),
            display: 'h1',
            stat: 'div',
            statSm: 'div',
            subtitle1: 'p',
            subtitle2: 'p',
            eyebrow: 'p',
            kindLabel: 'span',
            micro: 'span',
          },
        },
        styleOverrides: {
          root: {
            variants: [
              { props: { color: 'primary' }, style: { color: t.link } },
              ...STATUSES.map((status) => ({
                props: { color: status },
                style: { color: statusRole(t, status, 'Text') },
              })),
            ],
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            variants: STATUSES.map((status) => ({
              props: { color: status },
              style: { color: statusRole(t, status, 'Fg') },
            })),
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            // MUI tints the hover with `alpha(<colour>)`; the brand's state layer replaces it.
            // TMMAPP-0099 (visual contract §8): round, `text2`, and `link` on hover. The fill is the
            // `hover` layer, not the prototype's `line` (no pair on a `line` fill is guaranteed).
            '--IconButton-hoverBg': t.hover,
            variants: [
              {
                props: { color: 'default' },
                style: {
                  color: t.text2,
                  '@media (hover: hover)': { '&:hover': { color: t.link } },
                },
              },
              ...STATUSES.map((status) => ({
                props: { color: status },
                style: { color: statusRole(t, status, 'Fg') },
              })),
            ],
          },
        },
      },
      // TMMAPP-0037: MUI's own neutrals and render-time derivations (`alpha(` / `emphasize(` /
      // `lighten(` / `darken(` / `grey[`) replaced by brand roles. Tooltips and snackbars are the
      // inverse of `text / surface` (guaranteed; contrast is symmetric).
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: t.text, color: t.surface },
          arrow: { color: t.text },
        },
      },
      MuiSnackbarContent: {
        styleOverrides: {
          // TMMAPP-0099 (visual contract §11): radius `control`, padding 14 18, 14px, gap 20, at
          // most 420 wide; the Dismiss action 13/500, sentence case.
          root: {
            backgroundColor: t.text,
            color: t.surface,
            boxShadow: t.elevation.dialog,
            borderRadius: t.radius.control,
            padding: '14px 18px',
            fontSize: 14,
            lineHeight: '21px',
            columnGap: 20,
            maxWidth: 420,
            // The inverse surface: a label inside paints the snackbar's own ink.
            [TEXT_LABEL]: 'currentColor',
            flexWrap: 'nowrap',
            // TMMAPP-0055: an action paints the snackbar's `surface` ink on `text`, never a fill.
            '& .MuiSnackbarContent-action': {
              ...quietActionSlot,
              marginInlineEnd: 0,
              paddingInlineStart: 0,
              '& .MuiButton-root': { fontSize: 13, fontWeight: 500, textTransform: 'none' },
            },
            '& .MuiSnackbarContent-message': { padding: 0 },
          },
        },
      },
      // Every raised surface — menus, listboxes, popovers and dialogs (TMMAPP-0040) — paints
      // `surfaceRaised`: text, link and <status>Text are guaranteed on it, and since SASATH-0350
      // so are the control boundaries (lineStrong, focusRing, brand, accent, <status>Fg) and the
      // text / link state layers.
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: t.surfaceRaised,
            boxShadow: t.elevation.raised,
            ...raisedGuards,
          },
        },
      },
      // The modal backdrop paints the brand's `scrim` (no contrast pair: nothing is read on it);
      // the invisible backdrop of menus and popovers stays transparent.
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: t.scrim,
            variants: [{ props: { invisible: true }, style: { backgroundColor: 'transparent' } }],
          },
        },
      },
      // TMMAPP-0099 (visual contract §11): a menu is `surfaceRaised` at radius `control` with the
      // dialog elevation. The two object-menu variants (page / card) are `objectMenuSlotProps`.
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: t.surfaceRaised,
            boxShadow: t.elevation.dialog,
            borderRadius: t.radius.control,
            ...raisedGuards,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundColor: t.surfaceRaised,
            boxShadow: t.elevation.raised,
            ...raisedGuards,
          },
          groupLabel: { backgroundColor: t.surfaceRaised },
          listbox: {
            '& .MuiAutocomplete-option': {
              '&.Mui-focused': { backgroundColor: t.hover },
              '&[aria-selected="true"]': {
                backgroundColor: t.selected,
                '&.Mui-focused': { backgroundColor: t.selected },
              },
            },
          },
        },
      },
      MuiListSubheader: {
        styleOverrides: { root: { backgroundColor: t.surfaceRaised, color: t.text2 } },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&.Mui-focusVisible': { backgroundColor: t.hover },
            '&.Mui-selected': {
              backgroundColor: t.selected,
              '&:hover, &.Mui-focusVisible': { backgroundColor: t.selected },
              // A selected row's icon is `text`, like its label (0049). `text2` is also guaranteed
              // under `selected` since SASATH-0357; `text` is kept for the selection's emphasis.
              '& .MuiListItemIcon-root': { color: t.text },
            },
          },
        },
      },
      MuiListItemButton: {
        defaultProps: { dense: compact },
        styleOverrides: {
          root: {
            '&.Mui-focusVisible': { backgroundColor: t.hover },
            '&.Mui-selected': {
              backgroundColor: t.selected,
              '&:hover, &.Mui-focusVisible': { backgroundColor: t.selected },
              // A selected row's icon is `text`, like its label (0049). `text2` is also guaranteed
              // under `selected` since SASATH-0357; `text` is kept for the selection's emphasis.
              '& .MuiListItemIcon-root': { color: t.text },
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: t.hover },
            variants: [
              {
                props: { color: 'primary' },
                style: {
                  '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: t.brandBoundary },
                },
              },
            ],
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: t.hover },
            variants: [
              {
                props: { color: 'primary' },
                style: { '&.Mui-checked': { color: t.brandBoundary } },
              },
            ],
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            color: t.surface,
            '&:hover': { backgroundColor: t.hover },
            '&.Mui-checked': { color: t.brandBoundary },
            '&.Mui-checked + .MuiSwitch-track': { backgroundColor: t.brandBoundary },
            // MUI's `.Mui-checked:hover` / `.Mui-checked.Mui-disabled` variants out-specify the
            // two rules above, so they are restated at the same specificity.
            '&.Mui-checked:hover': { backgroundColor: t.hover },
            '&.Mui-disabled, &.Mui-checked.Mui-disabled': { color: t.surface2 },
            '&.Mui-disabled + .MuiSwitch-track': { backgroundColor: t.lineStrong },
          },
          // The unchecked track is opaque `lineStrong` (3:1 on surface). Checked keeps MUI's 0.5
          // opacity over `brandBoundary`, so the thumb stays distinct from its track.
          track: { backgroundColor: t.lineStrong, opacity: 1 },
        },
      },
      // TMMAPP-0099 (TMM-T4 1.4): no opacity animation anywhere — MUI's `pulse` fades the
      // placeholder, so a skeleton moves a `wave` across it instead.
      MuiSkeleton: {
        defaultProps: { animation: 'wave' },
        styleOverrides: { root: { backgroundColor: t.line } },
      },
      // TMMAPP-0049: the track is transparent, so the bar sits on the canvas it is guaranteed on
      // (bg / surface / surfaceRaised), and a 1px inset `lineStrong` outline marks its extent
      // (a box-shadow, so layout doesn't shift). A `line`-filled track put the bar on a pair
      // saas-auth does not guarantee. Status bars paint `<status>Fg` (3:1 on canvases), not
      // the `<status>Fill` MUI would use.
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            boxShadow: `inset 0 0 0 1px ${t.lineStrong}`,
          },
          // On the bar slot itself (both bars), so it replaces MUI's `palette[color].main` and
          // the indeterminate bar's CSS-variable colour rather than competing with them.
          bar: {
            variants: [
              { props: { color: 'primary' }, style: { backgroundColor: t.brandBoundary } },
              ...STATUSES.map((status) => ({
                props: { color: status },
                style: { backgroundColor: statusRole(t, status, 'Fg') },
              })),
            ],
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          root: { variants: [{ props: { color: 'primary' }, style: { color: t.brandBoundary } }] },
        },
      },
      // TMMAPP-0099 (TMM-T6 1.4, visual contract §5): a `line` rule under the strip and a 2px
      // `brandBoundary` indicator; the strip is no taller than its tabs.
      MuiTabs: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${t.line}`,
            minHeight: 0,
            // A scrollable row's start button, disabled at scroll 0, reserves 40px of blank before the
            // first tab (TMMAPP-0105 §C 7): it takes no space until the row can scroll back.
            '& .MuiTabs-scrollButtons.Mui-disabled:first-of-type': { display: 'none' },
          },
          indicator: { backgroundColor: t.brandBoundary, height: 2 },
        },
      },
      MuiStepConnector: {
        styleOverrides: { line: { borderColor: t.line } },
      },
      // TMMAPP-0080: MUI paints an inactive step's circle `text.disabled` (`text3`) with a
      // `contrastText` (`brandInk`) digit — a pair saas-auth does not guarantee (3.44:1 on the green
      // fixture). The inactive circle is `surface2` with a `text` digit; the current step is `brand`
      // with a `brandInk` digit (a completed step keeps its `brand` check). TMMAPP-0082: `surface2` on
      // the page is nearly invisible, so the inactive circle gets a 1 px `lineStrong` ring (the pair
      // is audited in tests/contrast.ts). It is the circle's own stroke, 2 units wide on the circle's
      // edge so the svg's overflow clip leaves 1 px inside: an inset `box-shadow` on the svg box would
      // sit under the circle's fill and never show.
      MuiStepIcon: {
        styleOverrides: {
          root: {
            color: t.surface2,
            '&:not(.Mui-active):not(.Mui-completed) circle': {
              stroke: t.lineStrong,
              strokeWidth: 2,
            },
            '&.Mui-active, &.Mui-completed': { color: t.brand },
            '&.Mui-active .MuiStepIcon-text': { fill: t.brandInk },
          },
          text: { fill: t.text },
        },
      },
      // `backgroundImage: 'none'` removes MUI's dark-mode elevation overlay so paper surfaces
      // stay token-exact (a no-op in light mode, which has none). A content card (TMMAPP-0099,
      // TMM-T6 1.4: elevation and outlined Paper, Card included) has NO border: `surface` at
      // `radius.card` with the elevation's own shadow (`shadows[1]` is `elevation.card`); an
      // outlined card drops MUI's border and takes `elevation.card`. An `sx` border or shadow
      // still wins (0097 B1): it is a later class. `nested` is the escape for a Paper inside one.
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            // A Paper is the label canvas `surface`; a raised paper overrides it (`raisedGuards`).
            [TEXT_LABEL]: t.textLabel('surface'),
            '&.MuiPaper-nested': {
              border: 'none',
              boxShadow: 'none',
              backgroundColor: t.surface2,
              // `surface2` is not a label canvas: a label on it steps up to `text2` (guaranteed).
              [TEXT_LABEL]: t.text2,
              backgroundImage: 'none',
            },
            variants: [
              {
                props: { variant: 'outlined' },
                style: { border: 'none', boxShadow: t.elevation.card },
              },
            ],
          },
          rounded: { borderRadius: t.radius.card },
        },
      },
      // TMMAPP-0053: MUI's card hover / focus highlight is `currentColor` at `action.hoverOpacity`
      // (0.04), not the brand's state layer, and it is drawn over the content. It paints the
      // brand `hover` token at full opacity, beneath the content (the root isolates a stacking
      // context), so a clickable card's content sits on `surface+hover`, a guaranteed pair.
      MuiCardActionArea: {
        styleOverrides: {
          root: {
            isolation: 'isolate',
            '&:hover .MuiCardActionArea-focusHighlight': {
              opacity: 1,
              '@media (hover: none)': { opacity: 0 },
            },
            '&.Mui-focusVisible .MuiCardActionArea-focusHighlight': { opacity: 1 },
          },
          focusHighlight: { backgroundColor: t.hover, zIndex: -1 },
        },
      },
      MuiAppBar: {
        // `color: 'inherit'` so the root override below is the bar's color in both modes (a
        // `colorPrimary` slot would otherwise win with `palette.primary.main`).
        // TMMAPP-0099 (visual contract §2): `appBar` / `appBarInk` with the raised shadow, no rule.
        defaultProps: { color: 'inherit', elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: t.appBar,
            color: t.appBarInk,
            boxShadow: t.elevation.raised,
            // Not a label canvas: a label on the bar paints the bar's own ink.
            [TEXT_LABEL]: 'currentColor',
          },
        },
      },
      // TMMAPP-0099 (TMM-T6 1.4, visual contract §8): the `button` type (500, sentence case: v2 §7)
      // at radius `control`. Contained: padding 8 20, flat (v2 §7, TMMAPP-0155 item 6); small 7 12
      // at 12px, flat; destructive flat. Outlined: transparent, `link`, a grey 1px `line` border, padding
      // 7 14 at 12px, hovering `selected` (over `surface`, see `overSurface`). Text: `link`,
      // hovering `hover`; a dialog's dismiss is `text2` (`MuiDialogActions`).
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: t.radius.control,
            minHeight: compact ? 32 : 36,
            paddingInline: 16,
            // Text / outlined hover is the brand's state layer, not `alpha(<colour>)`.
            '&:hover': { '--variant-textBg': t.hover, '--variant-outlinedBg': t.hover },
            variants: [
              {
                props: { variant: 'contained', size: 'medium' },
                // Flat at every size and state (visual contract v2 §7, TMMAPP-0155 item 6).
                style: {
                  padding: '8px 20px',
                  boxShadow: 'none',
                  '&:hover, &:active, &.Mui-focusVisible': { boxShadow: 'none' },
                },
              },
              {
                props: { variant: 'contained', size: 'small' },
                style: {
                  padding: '7px 12px',
                  fontSize: 12,
                  lineHeight: '18px',
                  boxShadow: 'none',
                  '&:hover, &:active, &.Mui-focusVisible': { boxShadow: 'none' },
                },
              },
              {
                props: { variant: 'outlined' },
                style: {
                  padding: '7px 14px',
                  fontSize: 12,
                  lineHeight: '18px',
                  backgroundColor: 'transparent',
                  borderColor: t.line,
                  '&:hover': { background: overSurface(t.selected), borderColor: t.line },
                },
              },
              // The text hover is `--variant-textBg` (the root rule above), so an alert's or a
              // snackbar's quiet action slot can still clear it.
              {
                props: { variant: 'text', size: 'medium' },
                style: { padding: '8px 16px' },
              },
              // TMMAPP-0049: MUI paints a disabled contained button `text3` on
              // `action.disabledBackground` (`surface2`), a pair saas-auth does not guarantee. It
              // paints `surface` (text3 is guaranteed on it) with a 1px inset `lineStrong` outline
              // marking its extent on whatever canvas hosts it (bg / surface / surfaceRaised).
              // Same selector as MUI's rule, later in the sheet, so it replaces MUI's background
              // and the `disableElevation` box-shadow. A loading button is disabled too, but it is
              // busy, not unavailable (TMMAPP-0097 B3): it keeps its own fill with the indicator
              // in the fill's ink, and MUI's transparent label stays transparent under it.
              {
                props: { variant: 'contained' },
                style: {
                  '&.Mui-disabled:not(.MuiButton-loading)': {
                    color: t.text3,
                    backgroundColor: t.surface,
                    boxShadow: `inset 0 0 0 1px ${t.lineStrong}`,
                  },
                  '&.MuiButton-loading': {
                    backgroundColor: 'var(--variant-containedBg)',
                    boxShadow: 'none',
                    '& .MuiButton-loadingIndicator': { color: 'var(--variant-containedColor)' },
                  },
                },
              },
              // MUI borders a disabled outlined button in `surface2`; `lineStrong` is the
              // guaranteed boundary on every canvas.
              {
                props: { variant: 'outlined' },
                style: { '&.Mui-disabled': { borderColor: t.lineStrong } },
              },
              {
                props: { variant: 'contained', color: 'primary' },
                style: { '&:hover': { backgroundColor: t.brandHover } },
              },
              {
                props: { variant: 'contained', color: 'secondary' },
                style: { '&:hover': { backgroundColor: t.accentHover } },
              },
              // Brand-coloured labels on a surface are `link` (guaranteed 4.5:1); the outlined
              // button's border is the prototype's grey `line` (its label identifies it, like a
              // text button's). The accent has no text role, so secondary labels are `text`.
              { props: { variant: 'text', color: 'primary' }, style: { color: t.link } },
              {
                props: { variant: 'outlined', color: 'primary' },
                style: { color: t.link, borderColor: t.line },
              },
              { props: { variant: 'text', color: 'secondary' }, style: { color: t.text } },
              {
                props: { variant: 'outlined', color: 'secondary' },
                style: { color: t.text, borderColor: t.line },
              },
              ...STATUSES.flatMap((status) => [
                {
                  props: { variant: 'contained' as const, color: status },
                  style: {
                    backgroundColor: statusRole(t, status, 'Fill'),
                    color: statusRole(t, status, 'Ink'),
                    boxShadow: 'none',
                    '&:hover, &:active, &.Mui-focusVisible': { boxShadow: 'none' },
                  },
                },
                {
                  props: { variant: 'text' as const, color: status },
                  style: { color: statusRole(t, status, 'Text') },
                },
                // A status outlined button keeps its `<status>Fg` border (the colour is part of
                // the destructive cue) and hovers with the `hover` layer, where `<status>Text` is
                // guaranteed (it is not under `selected`).
                {
                  props: { variant: 'outlined' as const, color: status },
                  style: {
                    color: statusRole(t, status, 'Text'),
                    borderColor: statusRole(t, status, 'Fg'),
                    '&:hover': {
                      background: 'none',
                      backgroundColor: t.hover,
                      borderColor: statusRole(t, status, 'Fg'),
                    },
                  },
                },
              ]),
            ],
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            height: compact ? 22 : 24,
            borderRadius: PILL_RADIUS,
            fontSize: 12,
            fontWeight: 500,
            // The delete icon takes the chip's label colour (MUI fades it with `alpha(`).
            '& .MuiChip-deleteIcon': { color: 'inherit' },
            variants: [
              {
                props: { variant: 'outlined', color: 'default' },
                style: { borderColor: t.lineStrong },
              },
              // TMMAPP-0054: MUI hovers a clickable outlined status chip with
              // `alpha(<status>.main, hoverOpacity)`, not the brand's state layer. Every clickable
              // outlined chip hovers and focuses with the brand `hover` over its host canvas, where
              // `text`, `link`, `<status>Text` and the boundaries are guaranteed (SASATH-0358), and
              // keyboard focus adds the `focusRing` outline on that canvas.
              {
                props: { variant: 'outlined' },
                style: {
                  '&.MuiChip-clickable:hover, &.MuiChip-clickable.Mui-focusVisible, &.MuiChip-deletable.Mui-focusVisible':
                    { backgroundColor: t.hover },
                  '&.Mui-focusVisible': {
                    outline: `2px solid ${t.focusRing}`,
                    outlineOffset: 2,
                  },
                },
              },
              // TMMAPP-0053: MUI fills a default chip with the translucent `selected` layer, which
              // composites over whatever hosts it — in a hovered row, `selected` over `hover`, a
              // double layer no guaranteed pair models. The fill is opaque `surface2`, on which
              // `text` (label and icon) is guaranteed whatever the row does. A clickable one keeps
              // it when hovered or focused (MUI would re-tint it with `alpha(selected)`); hover is
              // a 1px inset `lineStrong` outline marking its extent on the host canvas
              // (TMMAPP-0054, no layout shift), and keyboard focus a `focusRing` outline.
              {
                props: { variant: 'filled', color: 'default' },
                style: {
                  backgroundColor: t.surface2,
                  '&.MuiChip-clickable:hover': {
                    backgroundColor: t.surface2,
                    boxShadow: `inset 0 0 0 1px ${t.lineStrong}`,
                  },
                  '&.Mui-focusVisible': {
                    backgroundColor: t.surface2,
                    outline: `2px solid ${t.focusRing}`,
                    outlineOffset: 2,
                  },
                },
              },
              // MUI paints a default chip's own-colour icon `grey[700]` / `grey[300]`. It paints
              // `text`, the label's colour: guaranteed on the filled chip's `surface2`, on the
              // outlined chip's canvas and under `hover` (TMMAPP-0050). An icon with its own
              // `color` keeps it, as in MUI.
              {
                props: (props) => ownDefaultIcon(props),
                style: { '& .MuiChip-icon': { color: t.text } },
              },
              {
                props: { variant: 'outlined', color: 'primary' },
                style: { color: t.link, borderColor: t.brandBoundary },
              },
              ...STATUSES.flatMap((status) => [
                {
                  props: { variant: 'filled' as const, color: status },
                  style: {
                    backgroundColor: statusRole(t, status, 'Fill'),
                    color: statusRole(t, status, 'Ink'),
                  },
                },
                {
                  props: { variant: 'outlined' as const, color: status },
                  style: {
                    color: statusRole(t, status, 'Text'),
                    borderColor: statusRole(t, status, 'Fg'),
                  },
                },
              ]),
            ],
          },
          label: { paddingInline: 10 },
        },
      },
      // TMMAPP-0099 (TMM-T6 1.4, visual contract §5): padding 10 18 at 14/500, no uppercase.
      // Unselected `text2` with no fill; hover turns the text `link` (no hover fill); the selected
      // tab is a `surface` fill with `link` text, a white tab on the grey page.
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 0,
            minWidth: 0,
            padding: '10px 18px',
            fontSize: 14,
            lineHeight: '21px',
            fontWeight: 500,
            letterSpacing: 0,
            textTransform: 'none',
            color: t.text2,
            // A touch tap leaves `:hover` stuck on the tapped tab (TMMAPP-0097 B4).
            '@media (hover: hover)': { '&:hover': { color: t.link } },
            '&.Mui-selected': { color: t.link, backgroundColor: t.surface },
          },
        },
      },
      // TMMAPP-0099 (visual contract §10): collapsed borders; `size="dense"` is the envelope register's
      // tighter cells. Body cells are 12 14 (6 14 compact); a hovered row is the `hover` layer.
      MuiTable: {
        defaultProps: { size: 'small' },
        styleOverrides: { root: { borderCollapse: 'collapse' } },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&.MuiTableRow-hover:hover': { backgroundColor: t.hover },
            '&.Mui-selected': {
              backgroundColor: t.selected,
              '&:hover': { backgroundColor: t.selected },
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: 14,
            lineHeight: '21px',
            padding: compact ? '6px 14px' : '12px 14px',
            borderColor: t.line,
            borderBottom: `1px solid ${t.line}`,
            variants: [
              {
                props: { size: 'dense' },
                style: { paddingInline: 9 },
              },
            ],
          },
          head: {
            padding: '10px 14px',
            fontSize: 12,
            lineHeight: '18px',
            fontWeight: 500,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            color: t.text2,
            backgroundColor: t.surface2,
            borderBottomColor: t.line,
            variants: [{ props: { size: 'dense' }, style: { padding: '10px 9px' } }],
          },
        },
      },
      // TMMAPP-0099 (visual contract §14): the label sits above the field (12px `text2`, gap 6),
      // never floating, and the outline has no notch. The box is a 1px `lineStrong` boundary (a
      // field identified by its border needs 3:1, which `line` is not), padding 10 12 at 14px; focus
      // is the 2px `focusRing`. Helper text is `caption` in `text3`. Label wiring is MUI's own.
      MuiTextField: { defaultProps: { variant: 'outlined', size: 'small' } },
      MuiInputLabel: {
        defaultProps: { shrink: true },
        styleOverrides: {
          root: {
            position: 'static',
            transform: 'none',
            maxWidth: '100%',
            marginBottom: 6,
            fontSize: 12,
            lineHeight: '18px',
            fontWeight: 400,
            color: t.text2,
            whiteSpace: 'normal',
            overflow: 'visible',
            pointerEvents: 'auto',
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            // The prototype keeps a focused field's label `text2` (P:2013); the 2px `focusRing`
            // marks the focus.
            '&.Mui-focused': { color: t.text2 },
            '&.Mui-error': { color: t.errorText },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: 12,
            lineHeight: '18px',
            marginInline: 0,
            color: label,
            '&.Mui-error': { color: t.errorText },
          },
        },
      },
      MuiOutlinedInput: {
        defaultProps: { notched: false },
        styleOverrides: {
          root: {
            borderRadius: t.radius.control,
            fontSize: 14,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: t.lineStrong, top: 0 },
            '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: t.focusRing },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: t.errorFg },
          },
          input: {
            padding: '10px 12px',
            height: '21px',
            '&.MuiInputBase-inputSizeSmall': { padding: '10px 12px' },
          },
          multiline: { padding: '11px 13px', '& textarea': { lineHeight: 1.6 } },
        },
      },
      MuiSelect: {
        defaultProps: { IconComponent: SelectCaret },
        styleOverrides: { icon: { top: 'calc(50% - 10px)', color: t.text2 } },
      },
      // TMMAPP-0099 (visual contract §5): a segmented toggle is a `line`-bordered group at radius
      // `control`; unselected segments are `text2`, the selected one a `selected` fill with `link`
      // text (painted over `surface`: see `overSurface`). Padding 7 16 at 13/500; `size="small"`
      // is the filter row's 6 14 at 12/500, sentence case.
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: { borderRadius: t.radius.control },
          grouped: { borderColor: t.line },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontSize: 13,
            lineHeight: '20px',
            fontWeight: 500,
            letterSpacing: 0,
            padding: '7px 16px',
            color: t.text2,
            borderColor: t.line,
            '&.Mui-disabled': { borderColor: t.line },
            '&:hover': { backgroundColor: t.hover },
            '&.Mui-selected': {
              background: overSurface(t.selected),
              color: t.link,
              '&:hover': { background: overSurface(t.selected) },
            },
            variants: [
              {
                props: { size: 'small' },
                style: {
                  padding: '6px 14px',
                  fontSize: 12,
                  lineHeight: '18px',
                },
              },
            ],
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: t.surfaceRaised,
            borderRadius: t.radius.dialog,
            boxShadow: t.elevation.dialog,
            ...raisedGuards,
          },
        },
      },
      // TMMAPP-0099 (supersedes 0097 B5's values): the title is `h2` (26/30 400 since TMMAPP-0150),
      // padding 20 24; an onboarding dialog passes `variant="h1"` (the display option). A title focused by code (a
      // dialog's opening focus) shows no ring; keyboard focus does.
      MuiDialogTitle: {
        defaultProps: { variant: 'h2' },
        styleOverrides: {
          root: {
            padding: '20px 24px',
            // A `DialogSubtitle` directly after the title joins its block (P:2010).
            '&:has(+ [data-dialog-subtitle])': { paddingBottom: 4 },
            '&:focus:not(:focus-visible)': { outline: 'none' },
            '&:focus-visible': { outline: `2px solid ${t.focusRing}`, outlineOffset: 2 },
          },
        },
      },
      // Content (P:2012): the field block is 16 24 0, so the actions' 20 is the gap below it; the
      // last block (no actions after it) keeps a 20 bottom. A `dividers` block keeps MUI's padding.
      MuiDialogContent: {
        styleOverrides: {
          root: {
            variants: [
              {
                props: { dividers: false },
                style: { padding: '16px 24px 0', '&:last-child': { paddingBottom: 20 } },
              },
            ],
          },
        },
      },
      // Actions: padding 20 16 16, right-aligned, gap 8. The dismiss — the FIRST action, a primary
      // text button (Cancel / Close / Keep …, §7) — is `text2`, padding 8 16, hovering `hover`:
      // done here once, not per dialog. Any other text action (Clear, Dry run) stays `link`, and a
      // status-coloured text button keeps its own colour.
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '20px 16px 16px',
            justifyContent: 'flex-end',
            gap: 8,
            '& > :not(style) ~ :not(style)': { marginInlineStart: 0 },
            '& > .MuiButton-text.MuiButton-colorPrimary:first-of-type': {
              color: t.text2,
              padding: '8px 16px',
              '&:hover': { backgroundColor: t.hover },
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: t.radius.control,
            fontSize: 14,
            lineHeight: '20px',
            // Not a label canvas (a status wash): a label inside paints the alert's own ink.
            [TEXT_LABEL]: 'currentColor',
            // TMMAPP-0054: an action in the action or message slot paints the alert's own ink
            // (`<status>Text` on `<status>Soft`, `<status>Ink` on `<status>Fill`, `<status>Text`
            // on the outlined alert's canvas) and never fills.
            '& .MuiAlert-action, & .MuiAlert-message': quietActionSlot,
            variants: STATUSES.flatMap((status) => [
              {
                props: { variant: 'standard' as const, severity: status },
                style: {
                  backgroundColor: statusRole(t, status, 'Soft'),
                  color: statusRole(t, status, 'Text'),
                  // `<status>Fg` is guaranteed only on the canvases; on the soft wash the
                  // guaranteed role is `<status>Text` (4.5:1, TMMAPP-0049).
                  '& .MuiAlert-icon': { color: statusRole(t, status, 'Text') },
                },
              },
              {
                props: { variant: 'outlined' as const, severity: status },
                style: {
                  color: statusRole(t, status, 'Text'),
                  borderColor: statusRole(t, status, 'Fg'),
                  '& .MuiAlert-icon': { color: statusRole(t, status, 'Fg') },
                },
              },
              {
                props: { variant: 'filled' as const, severity: status },
                style: {
                  backgroundColor: statusRole(t, status, 'Fill'),
                  color: statusRole(t, status, 'Ink'),
                },
              },
            ]),
          },
          // MUI draws the icon at `opacity: 0.9`; the audit measures the full-opacity role, so
          // paint what is audited (TMMAPP-0050).
          icon: { opacity: 1 },
        },
      },
      MuiToolbar: { defaultProps: { variant: compact ? 'dense' : 'regular' } },
    },
  });
}
